import React, { useEffect, useState, useCallback } from 'react';
import css from './css/QuestionBank.scss';
import commonCss from './css/SurveyStyle.scss';
import classnames from 'classnames';
import Icon from 'SF/MaterialIcon';
import StyledButton from 'common/StyledButton';
import ErrorMessage from 'common/ErrorMessage';
import QuestionBankTable from './QuestionBankTable';
import AddCustomQuestionModal from './AddCustomQuestionModal';
import { getWord } from 'utils/tenant';
import PropTypes from 'prop-types';
import useStarfishAPI from 'hooks/useStarfishAPI';
import Toast from 'common/Toast';

const QuestionBank = ({back}) => {
    const studentSurveys = getWord('studentSurveys', false);
    const studentSurvey = getWord('studentSurvey', false);

    const [getQuestionsResponse, getQuestionsAPI] = useStarfishAPI({ isNewAPI: true });
    const [questions, setQuestions] = useState(null);
    //questionIdToEdit: will either be the id of the question the user wants to edit,
    //or null if the user is adding a new question.
    const [questionIdToEdit, setQuestionIdToEdit] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [error, setError] = useState('');
    const [toastMessage, setToastMessage] = useState(null);

    const fetchQuestions = useCallback(() => {
        getQuestionsAPI('../api/student-survey-questions', { method: 'GET' });
    }, []);

    useEffect(() => {
        fetchQuestions();
    }, []);

    useEffect(() => {
        if (getQuestionsResponse.success) {
            setQuestions(getQuestionsResponse.data);
        } else if (!getQuestionsResponse.pending && getQuestionsResponse.error) {
            setError(`There was a problem fetching the ${studentSurvey} Question Bank. \
                If this error continues to appear, please contact Starfish support.`);
        }
    }, [getQuestionsResponse]);

    const editQuestion = (questionId) => {
        setQuestionIdToEdit(questionId);
        setIsModalOpen(true);
    };

    return (
        <>
            {error && <ErrorMessage message={error} onDismiss={() => { setError(''); fetchQuestions(); }} />}
            <div className={css['container']}>
                <div className={css['breadcrumbs']}>
                    <Icon icon={'keyboard_arrow_left'} />
                    <StyledButton asLink className={css['back-btn']} onClick={back} autoFocus>
                        <>Back to {studentSurveys}</>
                    </StyledButton>
                </div>
                <h1 className={css['header']}>Question Bank</h1>
                <div className={classnames(commonCss['instructional-text'], css['text-spacing'])}>
                    The questions below are available for future {studentSurvey} use.
                    You can only <span className={commonCss['bold']}>Edit</span> questions that have never been used in
                    a scheduled, open, or closed {studentSurvey}.
                </div>
                <div className={classnames(commonCss['instructional-text'], css['text-spacing'])}>
                    Don&apos;t see a question you want? Select <span className={commonCss['bold']}>New Custom Question</span> to
                    add a new question to the question bank.
                </div>
                <hr aria-hidden />

                <div className={css['add-edit-btns']}>
                    <StyledButton className={commonCss['styled-button']} onClick={() => setIsModalOpen(true)}>
                        <><Icon icon='add' aria-hidden /> New Custom Question</>
                    </StyledButton>
                </div>

                {questions && <QuestionBankTable questions={questions} editQuestion={(questionId) => editQuestion(questionId)} />}
            </div>
            <AddCustomQuestionModal isOpen={isModalOpen}
                                    closeModal={() => {setIsModalOpen(false); setQuestionIdToEdit(null);}}
                                    questionIdToEdit={questionIdToEdit}
                                    isEdit={!!questionIdToEdit}
                                    onSave={() => { setToastMessage("Question saved."); fetchQuestions(); }} />
            {!!toastMessage && <Toast message={toastMessage} onTimeout={() => setToastMessage(null)} />}
        </>
    );
}

QuestionBank.propTypes = {
    back: PropTypes.func.isRequired
};

export default QuestionBank;
